/* eslint-disable react/prop-types */
import { useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

//selectors/
import { getLoading } from "../../../../selectors/commonSelectors";
//constants
import { FIELDS } from "../../../../constants/PostFormFields";

//components

import { sortAccounts } from "../../../../utils/accounts";
import { countCharacter } from "../../../../utils/strings";

import YouTubeTags from "../YouTubeFields/YouTubeTags";
import PostTags from "../PostTags/PostTags";
import { storyTagFields } from "../PostTags/story-tag-fields";
import PostUrl from "../PostUrl/PostUrl";
import PostFirstComment from "../PostFirstComment/PostFirstComment";
import PostCaption from "../PostCaption/PostCaption";
import PinterestFields from "../PinterestFields/PinterestFields";
import YouTubeTitle from "../YouTubeFields/YouTubeTitle";
import YouTubeCategory from "../YouTubeFields/YouTubeCategory";
import GmbFields from "../GmbFields/GmbFields";
import { AltTextModal } from "../modals/alt-text";
import { PlatformFieldsOverlay } from "./PlatformFieldsOverlay";
import YouTubePrivacy from "../YouTubeFields/YouTubePrivacy";
import { LinkedinTargetAudienceField } from "../LinkedinTargetAudience/LinkedinTargetAudienceField";
import { useLinkedInTargeting } from "../LinkedinTargetAudience/use-linkedin-targeting";
import type { Audiences } from "../LinkedinTargetAudience/LinkedinTargetAudience";
import { SwitchIsStoryOrNotification } from "./SwitchIsStoryOrNotification";
import { useStoryCapability } from "../utils/story-platforms";
import { TiktokSwitchNotification } from "../TiktokFields/TiktokSwitchNotification";
import { ThreadsSwitchNotification } from "../threadsFields/ThreadsSwitchNotification";
import { StoryFields } from "./StoryFields";
import { TiktokSwitchAllows } from "../TiktokFields/TiktokSwitchAllows";
import { PostAlerts } from "./PostAlerts";
import ThreadsReplyControl from "../threadsFields/ThreadsReplyControl";
import { useVideoCover } from "./VideoCoverSelector/hook";
import { VideoCoverSelector } from "./VideoCoverSelector/VideoCoverSelector";
import { VideoReelToggle } from "./VideoReelToggle/VideoReelToggle";
import { InstagramPlacementControl } from "./InstagramPlacementControl/InstagramPlacementControl";
import { InstagramAiDisclosure } from "./InstagramAiDisclosure/InstagramAiDisclosure";
import { InstagramTrialReel } from "./InstagramTrialReel/InstagramTrialReel";
import { InstagramAudioPicker } from "./InstagramAudio/InstagramAudioPicker";
import { PostFieldError } from "../PostFieldError/PostFieldError";
import { mergeGmbPostFields } from "../../../../features/post/utils/gmb-post-fields";
import { PlatformOptionsCard } from "./platform-options-card";
import { flexColumn, flexColumnMb } from "./flex-columns";
import { Post } from "../../../../features/post";
import {
  FormPlatformType,
  FormValidationErrors,
  FormValidationWarnings,
  PostMediaFile,
  PlatformFields,
} from "../../../../shared/types/post";
import { PlatformSpec } from "../../../../constants/Platforms";
import { AccountType } from "../../../../shared/types/accounts";
import { BulkTabContext } from "../types";
import { SkedEditorHistory } from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";

const RawAltTextModal = AltTextModal as any;

export const SinglePlatformPostForm = (props: {
  post: Post;
  postFormValue: Post;
  errors: FormValidationErrors | undefined;
  warnings: FormValidationWarnings | undefined;
  platforms: PlatformSpec[];
  platformFields: PlatformFields;
  formAccounts: AccountType[];
  updateFields: (fields: Record<string, unknown>) => void;
  updateField: (field: string, value: unknown) => void;
  platformPost: any;
  selectedPlatform: FormPlatformType;
  visibleFields: string[];
  igAudioPickerOpen: boolean;
  onIgAudioPickerOpenChange: (open: boolean) => void;
  isSummary?: boolean;
  hasPostTags?: boolean;
  hasTikTokPosting: boolean;
  oldTiktokAccounts: AccountType[];
  ttIsNotification: boolean;
  hasThreadsPosting: boolean;
  oldThreadsAccounts: AccountType[];
  thIsNotification: boolean;
  showUrlField: boolean;
  bulkTab: BulkTabContext | undefined;
  onCoverPreviewChange?: (coverUrl: string | null) => void;
  captionEditHistory?: SkedEditorHistory;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
}) => {
  const {
    post,
    postFormValue,
    errors,
    warnings,
    platforms,
    platformFields,
    formAccounts,
    updateFields,
    updateField,
    platformPost,
    selectedPlatform,
    visibleFields,
    igAudioPickerOpen,
    onIgAudioPickerOpenChange,
    isSummary,
    hasPostTags,
    hasTikTokPosting,
    oldTiktokAccounts,
    ttIsNotification,
    hasThreadsPosting,
    oldThreadsAccounts,
    thIsNotification,
    showUrlField,
    bulkTab,
    onCoverPreviewChange,
    captionEditHistory,
    selectedContentLabel,
    accountGroupId,
  } = props;
  const [openAddAlt, setOpenAddAlt] = useState(false);
  const story = useStoryCapability(platforms);

  // When user clicks outside from PostForm we shows him confirmation window
  const ref = useRef(null);
  const isLoading = useSelector(getLoading);
  // Warning: If we are using this for creat single post we need to make sure user is initialized properly.
  const fieldsRef = useRef(null);
  const sortedAccounts = sortAccounts(Object.values(formAccounts || []));

  const linkedInTargeting = useLinkedInTargeting({
    accounts: postFormValue.accounts,
    liTargetEntities: (
      postFormValue as Post & {
        liTargetEntities?: Audiences;
      }
    ).liTargetEntities,
    onAudienceChange: (nextAudience) =>
      updateField(FIELDS.LI_TARGET_ENTITIES, nextAudience),
  });

  const allDisabled = sortedAccounts.length === 0 || isLoading || post.past;
  let captionHashtagCount = countCharacter(postFormValue?.caption ?? "", "#");
  let firstCommentHashtagCount = countCharacter(
    postFormValue?.firstcomment ?? "",
    "#"
  );
  const hasInsta = sortedAccounts.find(
    (account) => account.platformName === "instagram"
  );

  if (hasInsta) {
    captionHashtagCount = firstCommentHashtagCount =
      captionHashtagCount + firstCommentHashtagCount;
  }

  const {
    showVideoSelector,
    isLoading: isLoadingVideo,
    videoMedia,
    videoUrl,
    originalMediaUrl,
    lowQualityMediaUrl,
    thumbnails,
    thumbnailUrl,
    videoLoadingError,
    coverFrames,
    lazyBlobRef,
    videoDurationFromMeta,
  } = useVideoCover({
    platformPost,
    selectedPlatform,
    platforms: platforms,
    thumbCount: bulkTab ? 6 : 10,
  });

  useEffect(() => {
    if (!showVideoSelector) {
      onCoverPreviewChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVideoSelector]);

  if (!platformPost) {
    return "";
  }

  function handleMediaUpdate(mediaFiles: PostMediaFile[]) {
    updateField(FIELDS.MEDIA, mediaFiles);
  }

  function handleImageAltUpdate(value: string) {
    updateField(FIELDS.ALT_TEXT, value);
  }

  function handleOpenAddAlt() {
    setOpenAddAlt(!openAddAlt);
  }

  const {
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
  } = linkedInTargeting;

  // @codeme Break this up into smaller components
  // todo: remove all logic for clusterd
  return (
    <>
      {/* Wrapper for platform fields */}
      <>
        {/* Wrapper for platform fields */}
        <Container ref={ref}>
          {/* Overlay for a linked platform */}
          {selectedPlatform !== "LINKED" &&
            platforms.length > 1 &&
            (!platformFields[selectedPlatform] ||
              platformFields[selectedPlatform].unlinked !== true) && (
              <PlatformFieldsOverlay fieldsRef={fieldsRef} />
            )}
          {/*
            Story Toggle. A multi-channel story renders here too (PostForm
            routes every story to this form), so the switch stays reachable to
            turn the story off even when a selected channel cannot take one.
          */}
          {(postFormValue.isStory || story.canPost) && (
            <SwitchIsStoryOrNotification
              postFormValue={postFormValue}
              platforms={platforms}
              updateField={updateField}
              selectedPlatform={selectedPlatform}
              allDisabled={allDisabled}
            />
          )}

          <PostAlerts
            postFormValue={postFormValue}
            oldTiktokAccounts={oldTiktokAccounts}
            platforms={platforms}
            selectedPlatform={selectedPlatform}
            ttIsNotification={ttIsNotification}
            oldThreadsAccounts={oldThreadsAccounts}
            isThreadsPostingEnabled={true}
            thIsNotification={thIsNotification}
          />

          <TiktokSwitchNotification
            platforms={platforms}
            value={ttIsNotification}
            hasTikTokPosting={hasTikTokPosting}
            updateField={updateField}
            allDisabled={Boolean(allDisabled)}
            oldTiktokAccounts={oldTiktokAccounts}
          />
          <ThreadsSwitchNotification
            platforms={platforms}
            value={thIsNotification}
            hasThreadsPosting={hasThreadsPosting}
            updateField={updateField}
            allDisabled={Boolean(allDisabled)}
            oldThreadsAccounts={oldThreadsAccounts}
          />

          {/* A story is never a reel; the toggle would mark a 9:16 Facebook story video as one. */}
          {!postFormValue.isStory && (
            <VideoReelToggle
              mediaFiles={(postFormValue?.mediaFiles || []) as PostMediaFile[]}
              platforms={platforms}
              selectedPlatform={selectedPlatform}
              formAccounts={formAccounts}
              updateField={updateField}
              disabled={Boolean(allDisabled)}
              currentValue={platformPost?.videoPostType}
              platformPost={platformPost}
            />
          )}

          <InstagramPlacementControl
            mediaFiles={(postFormValue?.mediaFiles || []) as PostMediaFile[]}
            selectedPlatform={selectedPlatform}
            formAccounts={formAccounts}
            updateField={updateField}
            disabled={Boolean(allDisabled)}
            platformPost={platformPost}
          />

          <InstagramTrialReel
            mediaFiles={(postFormValue?.mediaFiles || []) as PostMediaFile[]}
            selectedPlatform={selectedPlatform}
            formAccounts={formAccounts}
            updateField={updateField}
            disabled={Boolean(allDisabled)}
            platformPost={platformPost}
          />

          <InstagramAudioPicker
            mediaFiles={(postFormValue?.mediaFiles || []) as PostMediaFile[]}
            selectedPlatform={selectedPlatform}
            formAccounts={formAccounts}
            updateField={updateField}
            expanded={igAudioPickerOpen}
            onExpandedChange={onIgAudioPickerOpenChange}
            disabled={Boolean(allDisabled)}
            platformPost={platformPost}
          />

          <InstagramAiDisclosure
            selectedPlatform={selectedPlatform}
            formAccounts={formAccounts}
            updateField={updateField}
            disabled={Boolean(allDisabled)}
            platformPost={platformPost}
          />

          {/*
            SKE-4544. What the server said about publishing this draft on
            Instagram, for everything it did not pin to one media file. The
            per-file half renders on the asset tiles instead.
          */}
          <PostFieldError
            fields={[FIELDS.IG_PREFLIGHT]}
            errors={errors}
            warnings={warnings}
          />

          {/* Fields for non-story */}
          {showVideoSelector && (
            <VideoCoverSelector
              disabled={false}
              videoUrl={videoUrl}
              originalMediaUrl={originalMediaUrl}
              lowQualityMediaUrl={lowQualityMediaUrl}
              isLoading={isLoadingVideo}
              videoMedia={videoMedia}
              thumbnails={thumbnails}
              updateFields={updateFields}
              platforms={platforms}
              selectedPlatform={selectedPlatform}
              videoCoverPreview={platformPost?.videoCoverPreview}
              videoCoverType={platformPost?.videoCoverType}
              videoCoverOriginalTimestamp={
                platformPost?.videoCoverOriginalTimestamp
              }
              videoCoverOriginalType={platformPost?.videoCoverOriginalType}
              videoCoverOriginal={platformPost?.videoCoverOriginal}
              originalCover={
                platformPost?.videoCoverOriginal ||
                platformPost?.thumbnailUrl ||
                thumbnailUrl
              }
              thumbnailUrl={platformPost?.thumbnailUrl}
              videoCoverImageTimestamp={platformPost?.videoCoverImageTimestamp}
              videoLoadingError={videoLoadingError}
              onCoverPreviewChange={onCoverPreviewChange}
              coverFrames={coverFrames}
              lazyBlobRef={lazyBlobRef}
              videoDurationFromMeta={videoDurationFromMeta}
            />
          )}

          {!postFormValue.isStory && (
            <div>
              {[
                FIELDS.YT_TITLE,
                FIELDS.YT_CATEGORY,
                FIELDS.YT_PRIVACY,
                FIELDS.YT_TAGS,
              ].some((field) => visibleFields.includes(field)) && (
                <PlatformOptionsCard
                  title="YouTube Options"
                  iconClassName="fa-brands fa-youtube text-red-500"
                >
                  {/* YouTube title */}
                  {visibleFields.includes(FIELDS.YT_TITLE) && (
                    <YouTubeTitleStyled
                      key={`ytTitle-${selectedPlatform}`}
                      title={`Video title (YouTube)`}
                      post={platformPost}
                      selectedPlatform={selectedPlatform}
                      platforms={platforms}
                      onChange={(val: string) =>
                        updateField(FIELDS.YT_TITLE, val)
                      }
                      errors={errors}
                      disabled={
                        allDisabled ||
                        (selectedPlatform !== "LINKED" &&
                          (!platforms || (platforms && platforms.length > 1)) &&
                          (!platformFields[selectedPlatform] ||
                            platformFields[selectedPlatform].unlinked !== true))
                      }
                    />
                  )}
                  {/* YouTube category */}
                  {visibleFields.includes(FIELDS.YT_CATEGORY) && (
                    <YouTubeCategoryStyled
                      key={`ytCategoryId-${selectedPlatform}`}
                      title={`Video category`}
                      post={platformPost}
                      selectedPlatform={selectedPlatform}
                      platforms={platforms}
                      selectedAccounts={formAccounts}
                      onChange={(val: string) =>
                        updateField(FIELDS.YT_CATEGORY, val)
                      }
                      errors={errors}
                      disabled={
                        allDisabled ||
                        (selectedPlatform !== "LINKED" &&
                          (!platforms || (platforms && platforms.length > 1)) &&
                          (!platformFields[selectedPlatform] ||
                            platformFields[selectedPlatform].unlinked !== true))
                      }
                    />
                  )}
                  {/* YouTube privacy */}
                  {visibleFields.includes(FIELDS.YT_PRIVACY) && (
                    <YouTubePrivacyStyled
                      key={`ytPrivacyId-${selectedPlatform}`}
                      title={`Video category`}
                      post={platformPost}
                      selectedPlatform={selectedPlatform}
                      platforms={platforms}
                      selectedAccounts={formAccounts}
                      onChange={(val: string) =>
                        updateField(FIELDS.YT_PRIVACY, val)
                      }
                      errors={errors}
                      disabled={
                        allDisabled ||
                        (selectedPlatform !== "LINKED" &&
                          (!platforms || (platforms && platforms.length > 1)) &&
                          (!platformFields[selectedPlatform] ||
                            platformFields[selectedPlatform].unlinked !== true))
                      }
                    />
                  )}
                  {visibleFields.includes(FIELDS.YT_TAGS) && (
                    <YouTubeTagsStyled
                      key={`ytTags-${selectedPlatform}`}
                      title="YouTube tags"
                      post={platformPost}
                      selectedPlatform={selectedPlatform}
                      platforms={platforms}
                      selectedAccounts={formAccounts}
                      onChange={(val: string) =>
                        updateField(FIELDS.YT_TAGS, val)
                      }
                      errors={errors}
                      disabled={
                        allDisabled ||
                        (selectedPlatform !== "LINKED" &&
                          (!platforms || (platforms && platforms.length > 1)) &&
                          (!platformFields[selectedPlatform] ||
                            platformFields[selectedPlatform].unlinked !== true))
                      }
                    />
                  )}
                </PlatformOptionsCard>
              )}
              {/* Pinterest boards */}
              {visibleFields.includes(FIELDS.PI_BOARDS) && (
                <PlatformOptionsCard
                  title="Pinterest Options"
                  iconClassName="fa-brands fa-pinterest text-red-600"
                >
                  <PinterestFieldsStyled
                    key={`piBoards-${selectedPlatform}`}
                    post={platformPost}
                    selectedPlatform={selectedPlatform}
                    platforms={platforms}
                    selectedAccounts={formAccounts}
                    onChange={updateField}
                    disabled={
                      allDisabled ||
                      (selectedPlatform !== "LINKED" &&
                        (!platforms || (platforms && platforms.length > 1)) &&
                        (!platformFields[selectedPlatform] ||
                          platformFields[selectedPlatform].unlinked !== true))
                    }
                  />
                </PlatformOptionsCard>
              )}
              {/* Caption */}
              {visibleFields.includes(FIELDS.CAPTION) && (
                <BlockMargin>
                  <PostCaption
                    key={`caption-${selectedPlatform}`}
                    post={platformPost}
                    accounts={postFormValue?.accounts}
                    platforms={platforms}
                    selectedPlatform={selectedPlatform}
                    onChange={(val) => updateField(FIELDS.CAPTION, val)}
                    errors={errors}
                    warnings={warnings}
                    hashtagCount={captionHashtagCount}
                    disabled={
                      allDisabled ||
                      (selectedPlatform !== "LINKED" &&
                        (!platforms || (platforms && platforms.length > 1)) &&
                        (!platformFields[selectedPlatform] ||
                          platformFields[selectedPlatform].unlinked !== true))
                    }
                    hasMoreThanOnelinkedInAccount={
                      hasMoreThanOnelinkedInAccount
                    }
                    selectedLinkedInAccountType={selectedLinkedInAccountType}
                    selectedLinkedUrn={selectedLinkedUrn}
                    formId={bulkTab?.formId}
                    captionEditHistory={captionEditHistory}
                    selectedContentLabel={selectedContentLabel}
                    accountGroupId={accountGroupId}
                    labelIds={postFormValue?.labelIds || []}
                  />
                </BlockMargin>
              )}

              {/* First Comment */}
              {visibleFields.includes(FIELDS.FIRST_COMMENT) && (
                <PostFirstCommentStyled
                  key={`first-comment-${selectedPlatform}`}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  hashtagCount={firstCommentHashtagCount}
                  onChange={(val: string) =>
                    updateField(FIELDS.FIRST_COMMENT, val)
                  }
                  errors={errors}
                  warnings={warnings}
                  disabled={
                    allDisabled ||
                    (selectedPlatform !== "LINKED" &&
                      (!platforms || (platforms && platforms.length > 1)) &&
                      (!platformFields[selectedPlatform] ||
                        platformFields[selectedPlatform].unlinked !== true))
                  }
                  hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
                  selectedLinkedInAccountType={selectedLinkedInAccountType}
                  selectedLinkedUrn={selectedLinkedUrn}
                  formId={bulkTab?.formId}
                  selectedContentLabel={selectedContentLabel}
                  accountGroupId={accountGroupId}
                  labelIds={postFormValue?.labelIds || []}
                />
              )}

              {showUrlField && (
                <PostUrlStyled
                  url={postFormValue.redirectUrl}
                  checkSetting={true}
                  errors={errors}
                  selectedPlatform={selectedPlatform}
                  selectedAccounts={formAccounts}
                  platforms={platforms}
                  title="URL"
                  onChange={(newUrl: string) => updateField(FIELDS.URL, newUrl)}
                  disabled={
                    allDisabled ||
                    (selectedPlatform !== "LINKED" &&
                      (!platforms || (platforms && platforms.length > 1)) &&
                      (!platformFields[selectedPlatform] ||
                        platformFields[selectedPlatform].unlinked !== true))
                  }
                />
              )}

              {/* TT other fields or URL */}
              {hasTikTokPosting && (
                <PlatformOptionsCard
                  title="TikTok Options"
                  iconClassName="fa-brands fa-tiktok"
                >
                  <TiktokSwitchAllows
                    postFormValue={postFormValue}
                    updateField={updateField}
                    allDisabled={Boolean(allDisabled)}
                    errors={errors}
                  />
                </PlatformOptionsCard>
              )}
              {/* Threads fields */}
              {hasThreadsPosting && (
                <PlatformOptionsCard
                  title="Threads Options"
                  iconClassName="fa-brands fa-threads"
                >
                  <ThreadsReplyControl
                    key={`thReplyControlId-${selectedPlatform}`}
                    title={`Reply Control`}
                    post={platformPost}
                    selectedPlatform={selectedPlatform}
                    platforms={platforms}
                    selectedAccounts={formAccounts}
                    onChange={(val: string) =>
                      updateField(FIELDS.TH_REPLY_CONTROL, val)
                    }
                    errors={errors}
                    disabled={
                      allDisabled ||
                      (selectedPlatform !== "LINKED" &&
                        (!platforms || (platforms && platforms.length > 1)) &&
                        (!platformFields[selectedPlatform] ||
                          platformFields[selectedPlatform].unlinked !== true))
                    }
                  />
                </PlatformOptionsCard>
              )}
              {/* URL */}
              {/* Tags */}
              {hasPostTags && (
                <PostTagsStyled
                  hasTikTokPosting={hasTikTokPosting}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  visibleFields={visibleFields}
                  onChange={updateField}
                  handleOpenAddAlt={handleOpenAddAlt}
                  isSummary={isSummary}
                />
              )}
            </div>
          )}
          {postFormValue.isStory && (
            <StoryFields
              postFormValue={postFormValue}
              errors={errors}
              platforms={platforms}
              updateField={updateField}
              platformPost={platformPost}
              selectedPlatform={selectedPlatform}
              allDisabled={allDisabled}
              visibleFields={visibleFields}
              formId={bulkTab?.formId}
            />
          )}
          {/*
            SKE-4539. Stories accept user tags: Meta added them on 9 July 2025
            for both image and video stories, coordinates optional. The block
            above is inside a `!isStory` branch, so stories need their own, with
            the field list narrowed to what Meta actually accepts there.

            The narrowing lives in `storyTagFields` rather than inline here.
            This is the form that renders every story, because a story is
            Instagram-only and therefore single-platform, so an inline copy is
            the version that actually executes while the extracted one is only
            the version under test.

            `isStory` comes from `postFormValue`, which gates this branch, so
            the rule reads one source for it rather than two.
          */}
          {postFormValue.isStory &&
            hasPostTags &&
            storyTagFields(
              { isStory: true, storyType: platformPost?.storyType },
              visibleFields
            ).length > 0 && (
              <PostTagsStyled
                post={platformPost}
                selectedPlatform={selectedPlatform}
                platforms={platforms}
                visibleFields={storyTagFields(
                  { isStory: true, storyType: platformPost?.storyType },
                  visibleFields
                )}
                onChange={updateField}
                handleOpenAddAlt={handleOpenAddAlt}
                isSummary={isSummary}
              />
            )}
          {/* GMB Fields */}
          {visibleFields.includes(FIELDS.GMB_TOPIC_TYPE) && (
            <PlatformOptionsCard
              title="Google Business Profile Options"
              iconClassName="fa-brands fa-google"
            >
              <GmbFieldsStyled
                className="flex-col"
                onChange={updateFields}
                post={mergeGmbPostFields(
                  postFormValue as unknown as Record<string, unknown>,
                  platformFields[selectedPlatform] as unknown as
                    Record<string, unknown> | undefined
                )}
                selectedPlatform={selectedPlatform}
                platforms={platforms}
                disabled={
                  allDisabled ||
                  (selectedPlatform !== "LINKED" &&
                    (!platforms || (platforms && platforms.length > 1)) &&
                    (!platformFields[selectedPlatform] ||
                      platformFields[selectedPlatform].unlinked !== true))
                }
              />
            </PlatformOptionsCard>
          )}
          {/*
            `isVisible` is "a LinkedIn account is selected", and that is enough
            here without also testing the platform. `PostForm.tsx:239` sets
            isSinglePlatform = platforms.length <= 1 and `:517` renders this
            form only when that holds, and `platforms` is derived from the
            selected accounts (`use-post-form.tsx:137-140`). So a single
            platform form carrying a LinkedIn account carries nothing else, and
            the old `platforms[0].TYPE === "LI"` test picked out the same posts.
            The cluster form runs the same rule, which is the point: one rule,
            one place.
          */}
          {linkedInTargeting.isVisible && (
            <PlatformOptionsCard
              title="LinkedIn Options"
              iconClassName="fa-brands fa-linkedin text-[#0a66c2]"
            >
              <LinkedinTargetAudienceField
                targeting={linkedInTargeting}
                disabled={Boolean(allDisabled)}
              />
            </PlatformOptionsCard>
          )}
        </Container>
      </>

      {openAddAlt && (
        <RawAltTextModal
          post={post}
          toggleAddAlt={handleOpenAddAlt}
          handleImageAltUpdate={handleImageAltUpdate}
          onSubmit={handleMediaUpdate as any}
          platforms={platforms}
        />
      )}
    </>
  );
};

const Container = styled.div`
  position: relative;
`;
const BlockMargin = styled.div`
  margin-bottom: 20px;
`;

const YouTubeTitleStyled = styled(YouTubeTitle as any)`
  ${flexColumnMb};
`;
const YouTubeCategoryStyled = styled(YouTubeCategory as any)`
  ${flexColumnMb};
`;
const YouTubePrivacyStyled = styled(YouTubePrivacy as any)`
  ${flexColumnMb};
`;
const PinterestFieldsStyled = styled(PinterestFields as any)`
  ${flexColumn};
`;
const PostFirstCommentStyled = styled(PostFirstComment as any)`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;
const PostUrlStyled = styled(PostUrl as any)`
  margin-bottom: 20px;
`;
const PostTagsStyled = styled(PostTags as any)`
  display: flex;
  margin-bottom: 1rem;
`;
const GmbFieldsStyled = styled(GmbFields as any)`
  display: flex;
  margin-top: 3rem;
`;
const YouTubeTagsStyled = styled(YouTubeTags as any)`
  ${flexColumnMb}
`;
